export const runnerProtocol = "effect-atom-runner-v1" as const;

export const runnerLimits = {
  sourceBytes: 100_000,
  testBytes: 200_000,
  timeoutMs: 5_000,
  outputEntries: 100,
  outputEntryBytes: 8_000,
} as const;

export interface ProblemRunRequest {
  readonly source: string;
  readonly publicTestSource: string;
  readonly evaluatorTestSource: string;
  readonly timeoutMs: number;
}

export type BrokerRequest =
  | {
      readonly protocol: typeof runnerProtocol;
      readonly kind: "run";
      readonly jobId: string;
      readonly request: ProblemRunRequest;
    }
  | {
      readonly protocol: typeof runnerProtocol;
      readonly kind: "cancel";
      readonly jobId: string;
    };

export interface CompilerDiagnostic {
  readonly category: "error" | "warning";
  readonly message: string;
  readonly line: number | null;
  readonly column: number | null;
}

export type TestSuite = "public" | "hidden";

export interface TestResult {
  readonly name: string;
  readonly suite: TestSuite;
  readonly status: "passed" | "failed";
  readonly durationMs: number;
  readonly message: string | null;
}

export interface ProblemRunResult {
  readonly status: ProblemRunStatus;
  readonly diagnostics: ReadonlyArray<CompilerDiagnostic>;
  readonly tests: ReadonlyArray<TestResult>;
  readonly output: ReadonlyArray<string>;
  readonly durationMs: number;
}

export type BrokerResponse =
  | {
      readonly protocol: typeof runnerProtocol;
      readonly kind: "ready";
    }
  | {
      readonly protocol: typeof runnerProtocol;
      readonly kind: "phase";
      readonly jobId: string;
      readonly phase: "compiling" | "running";
    }
  | {
      readonly protocol: typeof runnerProtocol;
      readonly kind: "result";
      readonly jobId: string;
      readonly result: ProblemRunResult;
    };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const byteLength = (value: string): number => new TextEncoder().encode(value).byteLength;

export const validateProblemRunRequest = (value: unknown): value is ProblemRunRequest => {
  if (
    !isRecord(value) ||
    typeof value.source !== "string" ||
    typeof value.publicTestSource !== "string" ||
    typeof value.evaluatorTestSource !== "string" ||
    typeof value.timeoutMs !== "number" ||
    !Number.isFinite(value.timeoutMs)
  ) {
    return false;
  }
  return (
    value.timeoutMs > 0 &&
    byteLength(value.source) <= runnerLimits.sourceBytes &&
    byteLength(value.publicTestSource) <= runnerLimits.testBytes &&
    byteLength(value.evaluatorTestSource) <= runnerLimits.testBytes
  );
};

export const decodeBrokerRequest = (value: unknown): BrokerRequest | null => {
  if (
    !isRecord(value) ||
    value.protocol !== runnerProtocol ||
    typeof value.jobId !== "string" ||
    value.jobId.length === 0 ||
    value.jobId.length > 128
  ) {
    return null;
  }
  if (value.kind === "cancel") {
    return {
      protocol: runnerProtocol,
      kind: "cancel",
      jobId: value.jobId,
    };
  }
  if (value.kind !== "run" || !validateProblemRunRequest(value.request)) {
    return null;
  }
  const request = value.request;
  return {
    protocol: runnerProtocol,
    kind: "run",
    jobId: value.jobId,
    request: {
      source: request.source,
      publicTestSource: request.publicTestSource,
      evaluatorTestSource: request.evaluatorTestSource,
      timeoutMs: request.timeoutMs,
    },
  };
};

const problemRunStatuses = new Set([
  "passed",
  "failed",
  "compiler_error",
  "runtime_error",
  "timed_out",
  "cancelled",
  "platform_error",
] as const);

export type ProblemRunStatus =
  typeof problemRunStatuses extends ReadonlySet<infer Status> ? Status : never;

export const decodeBrokerResponse = (value: unknown): BrokerResponse | null => {
  if (!isRecord(value) || value.protocol !== runnerProtocol) return null;
  if (value.kind === "ready") {
    return { protocol: runnerProtocol, kind: "ready" };
  }
  if (typeof value.jobId !== "string") return null;
  if (value.kind === "phase" && (value.phase === "compiling" || value.phase === "running")) {
    return {
      protocol: runnerProtocol,
      kind: "phase",
      jobId: value.jobId,
      phase: value.phase,
    };
  }
  if (value.kind !== "result" || !isRecord(value.result)) return null;
  const result = value.result;
  if (
    typeof result.status !== "string" ||
    !problemRunStatuses.has(result.status as ProblemRunStatus) ||
    !Array.isArray(result.diagnostics) ||
    !Array.isArray(result.tests) ||
    !Array.isArray(result.output) ||
    !result.output.every((entry) => typeof entry === "string") ||
    typeof result.durationMs !== "number" ||
    !Number.isFinite(result.durationMs)
  ) {
    return null;
  }
  return {
    protocol: runnerProtocol,
    kind: "result",
    jobId: value.jobId,
    result: result as unknown as ProblemRunResult,
  };
};
